if (!customElements.get('pastael-community-set-form')) {
  class PastaelCommunitySetForm extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-add-set]');
      if (!this.button) return;

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.onAdd = this.onAdd.bind(this);
      this.refreshAvailability = this.refreshAvailability.bind(this);
      this.button.addEventListener('click', this.onAdd);
      this.refreshAvailability();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onAdd);
    }

    getResolvedVariantRecord(chip, colorFilter) {
      const variantId = this.resolveChipVariantId(chip, colorFilter);
      if (!variantId) return null;

      const variants = this.parseVariantLookup(chip);
      const match = variants.find((variant) => Number(variant.id) === Number(variantId));
      if (match) return match;

      return {
        id: variantId,
        available: chip.dataset.variantAvailable !== 'false',
      };
    }

    isSetFullyAvailable(card) {
      const chips = card.querySelectorAll('[data-chip-item][data-variant-id]');
      if (!chips.length) return this.button.dataset.setAvailable === 'true';

      const colorFilter = this.getColorFilter();

      return Array.from(chips).every((chip) => {
        const variant = this.getResolvedVariantRecord(chip, colorFilter);
        return variant && variant.available !== false;
      });
    }

    isSetAvailable(card) {
      const colorFilter = this.getColorFilter();

      if (card && colorFilter !== 'all') {
        return this.isSetFullyAvailable(card);
      }

      return this.button.dataset.setAvailable === 'true';
    }

    refreshAvailability() {
      if (!this.button) return;

      const card = this.closest('[data-look-card]');
      const enabled = this.isSetAvailable(card);
      const label = this.button.querySelector('[data-add-set-label]');
      const price = this.button.querySelector('[data-add-set-price]');
      const addLabel = this.button.dataset.addSetLabelDefault || 'Add the set';
      const soldOutLabel = this.button.dataset.addSetLabelSoldOut || 'Sold out';

      this.button.disabled = !enabled;
      if (enabled) {
        this.button.removeAttribute('aria-disabled');
        this.button.classList.remove('is-sold-out');
        if (label) label.textContent = addLabel;
        if (price) {
          price.classList.remove('is-hidden');
          price.removeAttribute('hidden');
        }
      } else {
        this.button.setAttribute('aria-disabled', 'true');
        this.button.classList.add('is-sold-out');
        if (label) label.textContent = soldOutLabel;
        if (price) {
          price.classList.add('is-hidden');
          price.setAttribute('hidden', '');
        }
      }
    }

    getColorFilter() {
      const filters = this.closest('.pastael-packed-by-you')?.querySelector('pastael-packed-by-you-filters');
      return filters?.getActiveColorFilter?.() || 'all';
    }

    colorMatches(slug, filterSlug) {
      if (!slug || !filterSlug) return false;
      return slug === filterSlug || slug.includes(filterSlug) || filterSlug.includes(slug);
    }

    sizeMatches(sizeA, sizeB) {
      if (!sizeA || !sizeB) return true;

      const normalize = (value) =>
        String(value || '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const a = normalize(sizeA);
      const b = normalize(sizeB);
      if (!a || !b) return true;

      return a === b || a.includes(b) || b.includes(a);
    }

    slugify(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    parseVariantLookup(chip) {
      const raw = chip.getAttribute('data-variant-lookup') || chip.dataset.productVariants || '[]';

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    resolveChipVariantId(chip, colorFilter) {
      const defaultId = Number(chip.dataset.variantId);
      if (!defaultId) return null;

      if (!colorFilter || colorFilter === 'all') {
        return defaultId;
      }

      const variants = this.parseVariantLookup(chip);
      if (!variants.length) {
        return defaultId;
      }

      const chipSize = chip.dataset.sizeValue || '';
      const colorFilterSlug = this.slugify(colorFilter);

      const colorAndSizeMatch = variants.find((variant) => {
        if (variant.available === false) return false;
        const variantColorSlug = this.slugify(variant.color);
        if (!this.colorMatches(variantColorSlug, colorFilterSlug)) return false;
        return this.sizeMatches(variant.size, chipSize);
      });

      if (colorAndSizeMatch?.id) {
        return Number(colorAndSizeMatch.id);
      }

      const colorMatch = variants.find((variant) => {
        if (variant.available === false) return false;
        const variantColorSlug = this.slugify(variant.color);
        return this.colorMatches(variantColorSlug, colorFilterSlug);
      });

      if (colorMatch?.id) {
        return Number(colorMatch.id);
      }

      const colorAndSizeAny = variants.find((variant) => {
        const variantColorSlug = this.slugify(variant.color);
        if (!this.colorMatches(variantColorSlug, colorFilterSlug)) return false;
        return this.sizeMatches(variant.size, chipSize);
      });

      if (colorAndSizeAny?.id) {
        return Number(colorAndSizeAny.id);
      }

      return defaultId;
    }

    getItemsFromLookCard(card) {
      const colorFilter = this.getColorFilter();
      const items = [];
      const seenVariantIds = new Set();

      card.querySelectorAll('[data-chip-item][data-variant-id]').forEach((chip) => {
        const variantId = this.resolveChipVariantId(chip, colorFilter);
        if (!variantId || Number.isNaN(variantId) || seenVariantIds.has(variantId)) return;

        seenVariantIds.add(variantId);
        items.push({ id: variantId, quantity: 1 });
      });

      return items;
    }

    getItemsToAdd() {
      const lookCard = this.closest('[data-look-card]');
      if (lookCard) {
        const chipItems = this.getItemsFromLookCard(lookCard);
        if (chipItems.length) return chipItems;
      }

      try {
        return JSON.parse(this.button.getAttribute('data-items') || '[]');
      } catch (error) {
        return [];
      }
    }

    onAdd(event) {
      event.preventDefault();
      event.stopPropagation();

      this.refreshAvailability();
      if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;

      const items = this.getItemsToAdd();
      if (!items.length) return;

      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('is-loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const body = { items };
      const cartRoutes = window.routes || (typeof routes !== 'undefined' ? routes : null);

      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = JSON.stringify(body);

      fetch(`${cartRoutes?.cart_add_url || '/cart/add'}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error('[pastael-community-set-form]', response.description || response.message);
            this.button.classList.remove('is-loading');
            this.refreshAvailability();
            return;
          }

          if (!this.cart) {
            window.location = cartRoutes?.cart_url || '/cart';
            return;
          }

          this.cart.renderContents(response);

          const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
          if (pubSubEvents && typeof publish === 'function') {
            try {
              publish(pubSubEvents.cartUpdate, {
                source: 'pastael-community-set-form',
                cartData: response,
              });
            } catch (publishError) {
              console.error(publishError);
            }
          }
        })
        .catch((error) => {
          console.error('[pastael-community-set-form]', error);
        })
        .finally(() => {
          this.button.classList.remove('is-loading');
          this.refreshAvailability();
          if (this.cart?.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
        });
    }
  }

  customElements.define('pastael-community-set-form', PastaelCommunitySetForm);
}
